import { styled } from "@mui/material/styles";
import { Box, Typography, Button, Tab, Tabs } from "@mui/material";
import SuccessPaymentIcon from "@assets/svg/SuccessPayment.svg?react";
import { ArrowBack } from "@mui/icons-material";
import MsoolVector from "@assets/svg/msoolVector.svg?react";
import FailPayment from "@assets/svg/FailPayment.svg?react";
import ReTry from "@assets/svg/ReTry.svg?react";
import GuestPortalDetailsIconSvg from "@assets/svg/GuestPortalDetails.svg?react";

interface ContainerProps {
  $isPhone: boolean;
}

interface TextProps {
  $isPhone: boolean;
}

interface ButtonProps {
  $isPhone: boolean;
}

interface BadgeProps {
  $isPhone: boolean;
}

// ==============================
// Main Portal Container Styles
// ==============================

export const PortalMainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: "80px",
  maxWidth: "100%",
  margin: "0 auto",
  [theme.breakpoints.up('md')]: {
    maxWidth: "100%",
    paddingLeft: "20px",
    paddingRight: "20px"
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: "100%"
  }
}));

export const LogoContainer = styled(Box)(({ theme }) => ({
  marginTop: "56px",
  marginBottom: "56px",
  objectFit: "contain",
  cursor: "pointer",
  display: "flex",
  justifyContent: "center",
  [theme.breakpoints.up('md')]: {
    marginTop: "40px",
    marginBottom: "40px"
  }
}));

// ==============================
// Welcome Step Styles
// ==============================

export const WelcomeContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  [theme.breakpoints.up('md')]: {
    maxWidth: "450px",
    margin: "0 auto"
  }
}));

export const WelcomeTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "36px",
  color: "#331860",
  textAlign: "center",
  [theme.breakpoints.up('md')]: {
    fontSize: "32px",
    lineHeight: "48px"
  }
}));

export const WelcomeDescription = styled(Typography)(({ theme }) => ({
  marginTop: "16px",
  marginBottom: "16px",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "19px",
  color: "#331860",
  textAlign: "center",
  [theme.breakpoints.up('md')]: {
    fontSize: "18px",
    lineHeight: "24px",
    marginTop: "24px",
    marginBottom: "24px"
  }
}));

export const WelcomeButton = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  marginRight: "auto",
  marginLeft: "auto",
  marginTop: "56px",
  color: "white",
  fontWeight: 700,
  lineHeight: "24px",
  fontSize: "16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "48px",
  width: "95%",
  backgroundColor: "#5B3FFF",
  borderRadius: "8px",
  [theme.breakpoints.up('md')]: {
    height: "56px",
    fontSize: "18px",
    marginTop: "40px",
    maxWidth: "350px"
  }
}));

// ==============================
// Guest Portal Details Styles
// ==============================

export const GuestPortalContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    margin: "0 auto",
    padding: "20px"
  }
}));

// Desktop Layout Components
export const DesktopLayoutContainer = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up('md')]: {
    display: "flex",
    width: "100%",
    gap: "16px",
    margin: "0 auto",
    padding: "20px",
    maxWidth: "1440px"
  }
}));

export const DesktopImageSection = styled(Box)(({ theme }) => ({
  flex: "0 0 60%",
  display: "flex",
  flexDirection: "column",
  gap: "16px"
}));

export const DesktopContentSection = styled(Box)(({ theme }) => ({
  flex: "0 0 40%",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  paddingLeft: "20px"
}));

export const DesktopMainImage = styled('img')(({ theme }) => ({
  width: "100%",
  height: "400px",
  objectFit: "cover",
  borderRadius: "12px"
}));

export const DesktopImageGallery = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "8px",
  marginTop: "8px"
}));

export const DesktopGalleryImage = styled('img')(({ theme }) => ({
  width: "100%",
  height: "80px",
  objectFit: "cover",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "opacity 0.2s",
  "&:hover": {
    opacity: 0.8
  }
}));

export const DesktopPropertyTitle = styled(Typography)(({ theme }) => ({
  fontSize: "32px",
  fontWeight: 700,
  lineHeight: "38px",
  color: "#331860",
  marginBottom: "16px"
}));

export const DesktopPropertyFeatures = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "24px",
  marginBottom: "24px",
  flexWrap: "wrap"
}));

export const DesktopFeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "16px",
  fontWeight: 500,
  color: "#331860"
}));

export const DesktopInfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  borderRadius: "12px",
  padding: "20px",
  marginBottom: "16px"
}));

export const DesktopInfoCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  fontWeight: 700,
  color: "#331860",
  marginBottom: "12px",
  display: "flex",
  alignItems: "center",
  gap: "8px"
}));

export const DesktopInfoCardContent = styled(Box)(({ theme }) => ({
  fontSize: "14px",
  color: "#625783",
  lineHeight: "1.5"
}));

// Mobile Layout Container
export const MobileLayoutContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  [theme.breakpoints.up('md')]: {
    display: "none"
  }
}));

export const PropertyImage = styled('img')(({ theme }) => ({
  marginTop: "12px",
  width: "100%",
  height: "200px",
  objectFit: "cover"
}));

export const ContentWrapper = styled(Box)<{ isPhone: boolean }>(({ isPhone, theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  width: "100%"
}));

export const StyledTabs = styled(Tabs)({
  marginTop: "4px",
  borderBottom: "0.5px solid #8E98A1",
  width: "100%",
  "& .MuiButtonBase-root": {
    fontSize: "12px",
    padding: "8px",
    fontWeight: "bold",
  },
  "&.Mui-selected": {
    color: "green",
  },
});

export const StyledTab = styled(Tab)({
  "& .Mui-selected": {
    color: "green",
  },
});

export const TabContent = styled(Box)({
  marginBottom: "80px",
  paddingLeft: "24px",
  paddingRight: "24px",
});

// ==============================
// Property Details Section Styles
// ==============================

export const PropertyDetailsContainer = styled(Box)({});

export const PropertyTitle = styled(Typography)(({ theme }) => ({
  fontSize: "24px",
  fontWeight: 700,
  lineHeight: "28.8px",
  color: "#331860",
  marginTop: "24px",
  marginBottom: "24px",
  [theme.breakpoints.up('md')]: {
    fontSize: "28px",
    lineHeight: "34px"
  }
}));

export const PropertyDivider = styled(Box)({
  border: "1px solid #E2E8F0",
});

// ==============================
// Check-in/Check-out Details Styles
// ==============================

export const CheckInOutContainer = styled(Box)(({ theme }) => ({
  paddingBottom: "4px",
  paddingTop: "12px",
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.up('md')]: {
    padding: "20px 24px"
  }
}));

export const CheckInOutItem = styled(Box)({});

export const CheckInOutLabel = styled(Typography)({
  fontWeight: 700,
  fontSize: "10px",
  lineHeight: "12px",
  color: "#331860",
});

export const CheckInOutDate = styled(Typography)({
  fontWeight: 700,
  fontSize: "13.7px",
  lineHeight: "18px",
  color: "#331860",
  marginTop: "5.6px",
  marginBottom: "5.6px",
});

export const CheckInOutTime = styled(Typography)({
  fontWeight: 400,
  fontSize: "10px",
  lineHeight: "18px",
  color: "#625783",
});

// ==============================
// Map Section Styles
// ==============================

export const MapContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const MapFrame = styled("iframe")(({ theme }) => ({
  frameBorder: 0,
  width: "101%",
  height: "190px",
  borderRadius: "10px",
  [theme.breakpoints.up('md')]: {
    height: "250px"
  }
}));

export const MapHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
  marginTop: "24px",
});

export const MapTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "19.2px",
  color: "#331860",
  marginBottom: "8px",
});

export const MapAddress = styled(Typography)({
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "14.4px",
  color: "#331860",
  textAlign: "center",
  marginTop: "16px",
  marginBottom: "16px",
  opacity: 0.5,
});

export const MapButton = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  color: "white",
  fontWeight: 700,
  lineHeight: "24px",
  fontSize: "16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "48px",
  width: "100%",
  backgroundColor: "#5B3FFF",
  borderRadius: "8px",
  marginTop: "12px",
  [theme.breakpoints.up('md')]: {
    height: "56px",
    fontSize: "18px"
  }
}));

export const MapImageContainer = styled(Box)({
  borderRadius: "8px",
  overflow: "hidden",
  marginBottom: "12px",
});

// ==============================
// Passcode Section Styles
// ==============================

export const PasscodeContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const PasscodeHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const PasscodeTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "19.2px",
  color: "#331860",
  marginBottom: "8px",
});

export const PasscodeDescription = styled(Typography)({
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "14.4px",
  color: "#331860",
  textAlign: "center",
  marginTop: "16px",
  marginBottom: "16px",
});

export const PasscodeBox = styled(Box)({
  paddingLeft: "16px",
  paddingRight: "16px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  paddingTop: "16px",
  paddingBottom: "16px",
});

export const PasscodeText = styled(Typography)<{ hasLock: boolean; codeLength: number }>(
  ({ hasLock, codeLength }) => ({
    fontWeight: 900,
    fontSize: "12px",
    lineHeight: "14.4px",
    color: "#625783",
    textAlign: "center",
    letterSpacing: hasLock && codeLength > 1 ? 10 : undefined,
  })
);

// ==============================
// WiFi Section Styles
// ==============================

export const WifiContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const WifiHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
  marginTop: "24px",
});

export const WifiTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "19.2px",
  color: "#331860",
  marginBottom: "8px",
});

export const WifiSubContainer = styled(Box)({
  border: "1px solid var(--Border, #E2E8F0)",
  borderRadius: "8px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: "12px",
  paddingBottom: "12px",
  paddingLeft: "16px",
  paddingRight: "16px",
});

export const WifiText = styled(Typography)({
  color: "#625783",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
});

export const WifiSpacer = styled(Box)({
  height: "10px",
});

// ==============================
// Location Details Styles
// ==============================

export const LocationContainer = styled(Box)(({ theme }) => ({
  paddingTop: "13.6px",
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const LocationHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const LocationTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "23px",
  color: "#100A55",
  marginLeft: "4.8px",
  marginRight: "4.8px",
});

export const LocationAddress = styled(Typography)({
  color: "#8E98A1",
  fontSize: "12px",
  fontWeight: 500,
});

export const LocationDetailsRow = styled(Box)({
  display: "flex",
  marginTop: "12px",
});

export const LocationDetailsGroup = styled(Box)({
  display: "flex",
});

export const LocationItem = styled(Box)({
  marginLeft: "8px",
  marginRight: "8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

export const LocationItemLabel = styled(Typography)({
  color: "#100A55",
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "24px",
});

export const LocationItemValue = styled(Typography)({
  color: "#100A55",
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "24px",
});

// ==============================
// Instructions Section Styles
// ==============================

export const InstructionsContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const InstructionsHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const InstructionsTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "23px",
  color: "#100A55",
  marginLeft: "3.2px",
  marginRight: "3.2px",
});

export const InstructionPoint = styled(Typography)({
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "15px",
  color: "#331860",
  opacity: 0.4,
  marginBottom: "3.2px",
});

export const InstructionBullet = styled(Box)({
  fontSize: "18px",
  component: "span",
});

// ==============================
// Image Gallery Styles
// ==============================

export const ImageGalleryContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "24px",
  marginBottom: "24px",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  [theme.breakpoints.up('md')]: {
    padding: "24px"
  }
}));

export const ImageGalleryHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const ImageGalleryTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "23px",
  color: "#100A55",
  marginLeft: "3.2px",
  marginRight: "3.2px",
});

export const ImageGalleryGrid = styled(Box)({
  display: "flex",
  flexFlow: "wrap",
});

export const ImageGalleryItem = styled(Box)({
  marginLeft: "8px",
  marginRight: "8px",
});

export const GalleryImage = styled('img')(({ theme }) => ({
  width: "163.5px",
  height: "163.5px",
  borderRadius: "8px",
  [theme.breakpoints.up('md')]: {
    width: "200px",
    height: "200px"
  }
}));

// ==============================
// Contact Section Styles
// ==============================

export const ContactContainer = styled(Box)(({ theme }) => ({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginBottom: "24px",
  marginTop: "16px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  paddingTop: "16px",
  paddingBottom: "16px",
  justifyContent: "space-between",
  [theme.breakpoints.up('md')]: {
    padding: "24px",
    gap: "16px"
  }
}));

export const ContactButton = styled(Box)(({ theme }) => ({
  width: "49%",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  cursor: "pointer",
  [theme.breakpoints.up('md')]: {
    padding: "24px 16px",
    minHeight: "120px"
  }
}));

export const ContactButtonText = styled(Typography)({
  marginTop: "8px",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
  color: "#625783",
});

// ==============================
// Desktop Layout Specific Styles
// ==============================

// Gallery Image with Selection Border
export const DesktopGalleryImageSelected = styled(DesktopGalleryImage)<{ $isSelected: boolean }>(({ $isSelected }) => ({
  border: $isSelected ? "2px solid #5B3FFF" : "none"
}));

// Check-in/Check-out Section Styles
export const CheckInOutRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "8px"
});

export const CheckInOutTimeRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between"
});

// WiFi Section Styles
export const WifiRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "8px"
});

export const WifiRowLast = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
});

export const WifiIcon = styled(Box)({
  width: "20px",
  height: "20px"
});

export const CopyIcon = styled(Box)({
  cursor: "pointer",
  width: "16px",
  height: "16px"
});

// Access Code Section Styles
export const AccessCodeBox = styled(Box)<{ $codeLength: number }>(({ $codeLength }) => ({
  backgroundColor: "white",
  padding: "12px",
  borderRadius: "8px",
  textAlign: "center",
  fontSize: "18px",
  fontWeight: "bold",
  letterSpacing: $codeLength > 1 ? "normal" : "normal"
}));

export const AccessCodeIcon = styled(Box)({
  width: "20px",
  height: "20px"
});

// Instructions Section Styles
export const InstructionItem = styled(Box)({
  marginBottom: "8px"
});

export const InstructionsIcon = styled(Box)({
  width: "20px",
  height: "20px"
});

// Location Details Section Styles
export const LocationIcon = styled(Box)({
  width: "20px",
  height: "20px",
  marginTop: "-0.3px"
});

// Contact Section Styles
export const ContactRow = styled(Box)({
  display: "flex",
  gap: "12px"
});

export const ContactButtonStyled = styled(ContactButton)({
  width: "48%"
});

export const ContactIcon = styled(Box)({
  width: "24px",
  height: "24px"
});

export const ContactSpacer = styled(Box)({
  width: "15px",
});

// ==============================
// Facility Section Styles
// ==============================

export const FacilityContainer = styled(Box)({
  border: "1px solid #ECECEC",
  borderRadius: "8px",
  height: "64px",
  width: "76px",
  display: "flex",
  justifyContent: "space-between",
  paddingTop: "6.4px",
  paddingBottom: "4px",
  alignItems: "center",
  flexDirection: "column",
  marginLeft: "4px",
  marginRight: "4px",
});

export const FacilityText = styled(Typography)({
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "17px",
  color: "#100A55",
});

// ==============================
// Modal Styles
// ==============================

export const ModalContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: "16px",
  paddingBottom: "16px",
});

export const ModalContactButton = styled(Box)({
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  cursor: "pointer",
});

export const ModalContactText = styled(Typography)({
  marginTop: "8px",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
  color: "#625783",
});

export const ModalBackButton = styled(Box)({
  marginTop: "16px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "8px",
  paddingBottom: "8px",
  cursor: "pointer",
});

export const ModalBackText = styled(Typography)({
  marginTop: "8px",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
  color: "#625783",
});

// ==============================
// Passcode Modal Styles
// ==============================

export const PasscodeModalContainer = styled(Box)({
  paddingLeft: "16px",
  paddingRight: "16px",
  marginTop: "4px",
  marginBottom: "4px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
});

export const PasscodeModalHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
});

export const PasscodeModalTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "23px",
  color: "#100A55",
  marginLeft: "8px",
  marginRight: "8px",
});

export const PasscodeModalDescription = styled(Typography)({
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "14.4px",
  color: "#331860",
  textAlign: "center",
  marginTop: "16px",
  marginBottom: "16px",
  opacity: 0.4,
});

export const PasscodeModalBox = styled(Box)({
  paddingLeft: "16px",
  paddingRight: "16px",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  paddingTop: "16px",
  paddingBottom: "16px",
});

export const PasscodeModalText = styled(Typography)<{ codeLength: number }>(({ codeLength }) => ({
  fontWeight: 900,
  fontSize: "12px",
  lineHeight: "14.4px",
  color: "#625783",
  textAlign: "center",
  letterSpacing: codeLength > 1 ? 20 : undefined,
  marginTop: "4px",
}));

export const PasscodeModalBackButton = styled(Box)({
  marginTop: "16px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  paddingTop: "16px",
  paddingBottom: "16px",
  cursor: "pointer",
});

export const PasscodeModalBackText = styled(Typography)({
  marginTop: "0px",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "24px",
  color: "#625783",
});

export const Title = styled('span')<TextProps>`
  font-weight: 700;
  font-style: bold;
  font-size: ${({ $isPhone }) => (!$isPhone ? "32px" : "24px")};
  color: #09090b;
  text-align: center;
`;

export const Description = styled('span')<TextProps>`
  font-weight: 400;
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "16px")};
  text-align: center;
  color: #71717a;
  margin-inline: ${({ $isPhone }) => (!$isPhone ? "0px" : "25px")};
  padding-inline: ${({ $isPhone }) => (!$isPhone ? "32px" : "16px")};
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
`;

export const InfoBox = styled('span')<TextProps>`
  font-weight: 400;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "14px")};
  text-align: center;
  color: #09090b;
  margin-inline: ${({ $isPhone }) => (!$isPhone ? "0px" : "25px")};
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  padding: ${({ $isPhone }) => (!$isPhone ? "24px" : "16px")};
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
`;

export const StyledButton = styled('button')<ButtonProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "300px" : "100%")};
  height: ${({ $isPhone }) => (!$isPhone ? "56px" : "48px")};
  border-radius: 10.8px;
  background-color: #5b3fff;
  color: #fff;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "14px")};
  font-weight: 500;
  font-style: bold;
  text-align: center;
  gap: 8px;
  padding: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Badge = styled('div')<BadgeProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background-color: #f9f9fa;
  padding: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  border-radius: 12px;
  left: ${({ $isPhone }) => (!$isPhone ? "50%" : "auto")};
  margin-block: 20px;
`;

export const BadgeText = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "12px")};
  font-weight: 400;
  color: #00000075;
`;

export const SuccessIcon = styled(SuccessPaymentIcon)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
  height: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
`;

export const FailPaymentIcon = styled(FailPayment)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
  height: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
`;

export const ArrowIcon = styled(ArrowBack)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  height: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
`;

export const ReTryIcon = styled(ReTry)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  height: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
`;

export const MsoolIcon = styled(MsoolVector)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "64px" : "54px")};
  height: ${({ $isPhone }) => (!$isPhone ? "25px" : "21px")};
`;

// GuestPortalDetails styled components
export const DetailsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
`;

export const ImageContainer = styled('div')<ContainerProps>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ $isPhone }) => (!$isPhone ? "300px" : "271px")};
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ContentContainer = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  z-index: 1;
  transform: translateY(-10px);
  background-color: #ffffff;
  width: 100%;
  padding-inline: 16px;
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "100%")};
  margin: ${({ $isPhone }) => (!$isPhone ? "0 auto" : "0")};
`;

export const MainTitle = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "32px" : "25px")};
  font-weight: 700;
  color: #222222;
  padding-top: ${({ $isPhone }) => (!$isPhone ? "40px" : "33px")};
`;

export const FeaturesContainer = styled('div')<ContainerProps>`
  display: flex;
  align-items: center;
  gap: ${({ $isPhone }) => (!$isPhone ? "24px" : "20px")};
  padding-bottom: ${({ $isPhone }) => (!$isPhone ? "24px" : "21px")};
  padding-top: ${({ $isPhone }) => (!$isPhone ? "12px" : "10px")};
`;

export const FeatureItem = styled('span')<TextProps>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "12px")};
  font-weight: 400;
  color: #222222cc;
`;

export const DateContainer = styled('div')<ContainerProps>`
  display: flex;
  width: 100%;
  padding-block: ${({ $isPhone }) => (!$isPhone ? "20px" : "12px")};
  background-color: #f9f9fa;
  border-radius: 12px;
  justify-content: space-between;
  align-items: center;
  padding-inline: ${({ $isPhone }) => (!$isPhone ? "40px" : "30px")};
`;

export const DateItem = styled('span')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ $isPhone }) => (!$isPhone ? "8px" : "6px")};
`;

export const DateLabel = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "15px" : "10px")};
  font-weight: 700;
  color: #331860;
`;

export const DateValue = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 700;
  color: #331860;
`;

export const DateSubtext = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "15px" : "10px")};
  font-weight: 400;
  color: #625783;
`;

export const LineIconContainer = styled('span')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PaymentContainer = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  padding: ${({ $isPhone }) => (!$isPhone ? "24px" : "16px")};
  background-color: #f9f9fa;
  border-radius: 12px;
  gap: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
`;

export const PaymentTitle = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  font-weight: 500;
  color: #130080;
`;

export const PaymentRow = styled('span')`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

export const PaymentLabel = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 500;
  color: #435363;
`;

export const PaymentValue = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 400;
  color: #8e98a1;
`;

export const PaymentTotal = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 500;
  color: #222222;
`;

// Payment section without padding
export const PaymentSection = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  gap: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
`;

// PaymentSuccessDetails specific styled components
export const DetailsSection = styled('section')<ContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ $isPhone }) => (!$isPhone ? "24px" : "16px")};
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
  width: 100%;
`;

export const PropertyInfoContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

export const PropertyTextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: start;
`;

export const PropertyTitleSpan = styled('span')`
  font-size: 20px;
  font-weight: 700;
  color: #000000;
`;

export const PropertyLocation = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #000000b2;
`;

export const BookingDetailsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

export const BookingDetailsTitle = styled('span')`
  font-size: 14px;
  font-weight: 500;
  color: #000000;
`;

export const BookingDetailsValue = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
`;

export const BookingGuestsText = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// PaymentSuccessDetails.tsx styled components
export const PaymentSuccessContainer = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  gap: 16px;
  padding: 30px 16px 16px 16px;
  position: relative;
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
  margin: ${({ $isPhone }) => (!$isPhone ? "0 auto" : "0")};
`;

export const PaymentSuccessTitle = styled('span')<TextProps>`
  font-weight: 700;
  font-style: bold;
  font-size: ${({ $isPhone }) => (!$isPhone ? "32px" : "24px")};
  color: #09090b;
  text-align: center;
`;

export const PaymentSuccessDescription = styled('span')<TextProps>`
  font-weight: 400;
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "16px")};
  text-align: center;
  color: #71717a;
  margin-inline: ${({ $isPhone }) => (!$isPhone ? "0px" : "25px")};
  padding-inline: ${({ $isPhone }) => (!$isPhone ? "32px" : "16px")};
  width: 100%;
`;

export const PaymentSuccessInfoBox = styled('span')<TextProps>`
  font-weight: 400;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "14px")};
  text-align: center;
  color: #09090b;
  margin-inline: ${({ $isPhone }) => (!$isPhone ? "0px" : "25px")};
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  padding: ${({ $isPhone }) => (!$isPhone ? "24px" : "16px")};
  max-width: ${({ $isPhone }) => (!$isPhone ? "500px" : "auto")};
`;

export const PaymentSuccessButton = styled('button')<ButtonProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "300px" : "100%")};
  height: ${({ $isPhone }) => (!$isPhone ? "56px" : "48px")};
  border-radius: 10.8px;
  background-color: #5b3fff;
  color: #fff;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "14px")};
  font-weight: 500;
  font-style: bold;
  text-align: center;
  gap: 8px;
  padding: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PaymentSuccessBadge = styled('div')<BadgeProps>`
  position: absolute;
  bottom: ${({ $isPhone }) => (!$isPhone ? "30px" : "13px")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background-color: #f9f9fa;
  padding: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  border-radius: 12px;
  left: ${({ $isPhone }) => (!$isPhone ? "50%" : "auto")};
  transform: ${({ $isPhone }) => (!$isPhone ? "translateX(-50%)" : "none")};
`;

export const PaymentSuccessBadgeText = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "12px")};
  font-weight: 400;
  color: #00000075;
`;

export const PaymentSuccessIcon = styled(SuccessPaymentIcon)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
  height: ${({ $isPhone }) => (!$isPhone ? "120px" : "96px")};
`;

export const PaymentSuccessArrowIcon = styled(ArrowBack)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  height: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
`;

export const PaymentSuccessMsoolIcon = styled(MsoolVector)<TextProps>`
  width: ${({ $isPhone }) => (!$isPhone ? "64px" : "54px")};
  height: ${({ $isPhone }) => (!$isPhone ? "25px" : "21px")};
`;

export const PaymentSuccessFeaturesContainer = styled('div')<ContainerProps>`
  display: flex;
  align-items: center;
  gap: ${({ $isPhone }) => (!$isPhone ? "24px" : "20px")};
  padding-bottom: ${({ $isPhone }) => (!$isPhone ? "24px" : "21px")};
  padding-top: ${({ $isPhone }) => (!$isPhone ? "12px" : "10px")};
`;

export const PaymentSuccessFeatureItem = styled('span')<TextProps>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ $isPhone }) => (!$isPhone ? "16px" : "12px")};
  font-weight: 400;
  color: #222222cc;
`;

export const PaymentSuccessPaymentContainer = styled('div')<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  gap: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
`;

export const PaymentSuccessPaymentTitle = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "20px" : "16px")};
  font-weight: 500;
  color: #130080;
`;

export const PaymentSuccessPaymentRow = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

export const PaymentSuccessPaymentLabel = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 500;
  color: #435363;
`;

export const PaymentSuccessPaymentValue = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 400;
  color: #8e98a1;
`;

export const PaymentSuccessPaymentTotal = styled('span')<TextProps>`
  font-size: ${({ $isPhone }) => (!$isPhone ? "18px" : "14px")};
  font-weight: 500;
  color: #222222;
`;

export const PaymentSuccessDetailsSection = styled('section')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
`;

export const PaymentSuccessPropertyInfoContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

export const PaymentSuccessPropertyTextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: start;
`;

export const PaymentSuccessPropertyTitle = styled('span')`
  font-size: 20px;
  font-weight: 700;
  color: #000000;
`;

export const PaymentSuccessPropertyLocation = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #000000b2;
`;

export const PaymentSuccessBookingDetailsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 4px;
  width: 100%;
`;

export const PaymentSuccessBookingDetailsTitle = styled('span')`
  font-size: 14px;
  font-weight: 500;
  color: #000000;
`;

export const PaymentSuccessBookingDetailsText = styled('span')`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
`;

export const PaymentSuccessBookingGuestsText = styled('span')` 
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const PaymentSuccessDivider = styled('div')`
  width: 100%;
  margin-block: 16px;
  height: 1px;
  background-color: #e0e0e0;
`;

export const PaymentSuccessPaymentDivider = styled('div')`
  width: 100%;
  height: 1px;
  background-color: #dddddd;
`;

// GuestPortalDetails.tsx styled components
export const GuestPortalDetailsIconStyled = styled(GuestPortalDetailsIconSvg)`
  width: 100%;
  height: 100%;
`;

export const GuestPortalDetailsDivider = styled('div')`
  width: 100%;
  height: 1px;
  margin-bottom: 24px;
  background-color: #dddddd;
`;

export const GuestPortalDetailsDateDivider = styled('div')`
  width: 100%;
  height: 1px;
  margin-block: 24px;
  background-color: #dddddd;
`;

export const GuestPortalDetailsPaymentDivider = styled('div')`
  width: 100%;
  height: 1px;
  background-color: #dddddd;
`;

// GuestPortalCheckout Component Styles
export const CheckoutContainer = styled('div')(({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
  
  ${theme.breakpoints.up('md')} {
    padding: 30px;
  }
`);

export const PaymentSummaryContainer = styled('div')` 
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
`;

export const PaymentMethodsSection = styled('div')`
  display: flex;
  flex-direction: column;
`;

export const PaymentMethodsTitle = styled('span')`
  color: #130080;
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
  padding-bottom: 16px;
`;

export const PaymentMethodsList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const PaymentFormSection = styled('div')`
  margin-top: 20px;
`;

export const PaymentFormTitle = styled('span')`
  color: #130080;
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
  padding-bottom: 16px;
`;

export const PaymentFormContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

// PaymentMethodItem Component Styles
export const PaymentMethodItemContainer = styled('div')<{ $disabled: boolean }>(({ $disabled, theme }) => `
  display: flex;
  padding: 17px;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  opacity: ${$disabled ? 0.6 : 1};
  background-color: ${$disabled ? "#F8F9FA" : "transparent"};
  
  ${theme.breakpoints.up('md')} {
    padding: 24px;
  }
`);

export const PaymentMethodContent = styled('div')` 
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  margin-inline: 10px;
`;

export const PaymentMethodTitle = styled('span')<{ $disabled: boolean }>`
  color: ${({ $disabled }) => ($disabled ? "#9CA3AF" : "#020817")};
  font-size: 16px;
  font-weight: 700;
`;

export const PaymentMethodDescription = styled('span')<{ $disabled: boolean }>`
  color: ${({ $disabled }) => ($disabled ? "#D1D5DB" : "#64748B")};
  font-size: 12px;
  font-weight: 400;
`;

// Payment Method Icon Styles
export const CreditCardIconContainer = styled('div')`
  width: 40px;
  height: 40px;
  padding: 6px;
  background-color: #5b3fff0d;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ApplePayIconContainer = styled('div')`
  width: 40px;
  height: 40px;
  padding: 6px;
  background-color: #000000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Back Button Section Styles
export const BackButtonContainer = styled('div')(({ theme }) => `
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  margin-block: 24px;
  
  ${theme.breakpoints.up('md')} {
    margin-block: 30px;
  }
`);

export const BackButtonText = styled('span')`
  color: #435363;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`;

// GuestPortalSummary Component Styles
export const ButtonContainer = styled('div')(({ theme }) => `
  display: flex;
  justify-content: center;
  width: 100%;
  margin-block: 20px;
  
  ${theme.breakpoints.up('md')} {
    margin-block: 30px;
  }
`);

export const StyledBox = styled(Box)(({ theme }) => `
  margin-inline: 20px;
  display: flex;
  padding: 12px 24px;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  background: #5b3fff;
  color: #ffffff;
  cursor: pointer;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  width: 100%;
  max-width: 400px;
  
  ${theme.breakpoints.up('md')} {
    margin-inline: 0;
    max-width: 350px;
    height: 56px;
    font-size: 18px;
  }
`);

export const ButtonText = styled('span')`
  color: #fff;
  font-size: 16px;
  font-weight: 500;
`;

export const ButtonAmount = styled('span')`
  color: #fff;
  text-align: center;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  padding: 8px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.1);
`;

// GuestPortalCheckout Component Styles
export const StyledArrowBackIcon = styled(ArrowBack)<{ $isRTL: boolean }>`
  width: 24px;
  height: 24px;
  transform: ${({ $isRTL }) => ($isRTL ? "scaleX(-1)" : "none")};
  cursor: pointer;
`;

export const StyledDivider = styled('div')`
  background-color: #e0e0e0;
  margin-block: 20px;
`;
